import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useT } from "../../i18n/index";
import { Icon } from "../../ui/icons";
import { usePartySearch, useBookSummary, type PartyHit } from "./use-household";
import type { BookFilter, BookHouseholdSummary } from "./types";

// U1-F / Müşterilerim — the Müşteri 360 landing. Calm, list-first: the advisor's OWN book
// (GET /v1/advisors/me/book/summary?filter=…, masked + derived signals) is shown by default
// — no empty-until-search. A "N hane" count sits atop it, and three SERVER-SIDE filter tabs
// (Benim hanelerim · Aksiyon bekleyen · Care-hold) narrow the book without leaving the page.
// A SEARCH box on top still narrows by phone/name (GET /v1/parties/find); a hit that carries
// a household id links straight into the 360, otherwise it's shown but flagged. TCKN/health
// are never shown; phones are the server's MASKED value only (PII-in-DOM doctrine).

// The three book tabs (server-side filters). Order = default-first.
const BOOK_TABS: Array<{ id: BookFilter; key: "mine" | "actionPending" | "careHold" }> = [
  { id: "mine", key: "mine" },
  { id: "action-pending", key: "actionPending" },
  { id: "care-hold", key: "careHold" },
];

export function HouseholdHome() {
  const { t } = useT();
  const navigate = useNavigate();

  // ⌘K hands the typed query over as ?q=… — the palette used to discard it and drop the
  // advisor on an empty search box, making them type the customer's name a second time.
  // Seeded once; the form owns the value from then on.
  const [params] = useSearchParams();
  const handoff = params.get("q") ?? "";
  const [query, setQuery] = useState(handoff);
  const [submitted, setSubmitted] = useState(handoff);
  // Auto-detect intent: a query starting with + or a digit is a phone; otherwise a name.
  const mode: "phone" | "name" = /^[+\d]/.test(submitted.trim()) ? "phone" : "name";
  const search = usePartySearch(mode, submitted);
  const hits = search.data ?? [];

  // The default surface — the advisor's own book, filtered server-side by the active tab.
  const [filter, setFilter] = useState<BookFilter>("mine");
  const book = useBookSummary(filter);
  const bookRows = book.data?.households ?? [];
  const bookCount = book.data?.count ?? 0;

  const [rawId, setRawId] = useState("");
  const searching = submitted.trim().length > 0;

  return (
    <section className="surface hh-search">
      <header className="surface__head">
        <div>
          <h1 className="surface__title">{t("hh.heading")}</h1>
          <p className="surface__subtitle">{t("hh.search.label")}</p>
        </div>
      </header>

      {/* The primary flow — filter the list by phone or name. */}
      <form
        className="hh-search-form"
        style={{ display: "flex", gap: 10, alignItems: "stretch", maxWidth: 560 }}
        onSubmit={(e) => {
          e.preventDefault();
          setSubmitted(query);
        }}
      >
        <label className="k-search" style={{ flex: 1 }} aria-label={t("hh.search.label")}>
          <Icon name="search" size={18} />
          <input
            aria-label={t("hh.search.label")}
            data-testid="hh-search-input"
            placeholder={t("hh.search.placeholder")}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              // Clearing the box returns to the default book (no lingering results).
              if (e.target.value.trim() === "") setSubmitted("");
            }}
          />
        </label>
        <button
          type="submit"
          className="k-btn k-btn--primary"
          data-testid="hh-search-submit"
          disabled={!query.trim()}
        >
          {t("hh.search.go")}
        </button>
      </form>

      {searching ? (
        <div className="hh-results" data-testid="hh-results">
          <p className="surface__section-label">
            {mode === "phone" ? t("hh.search.byPhone") : t("hh.search.byName")}
          </p>
          {search.isLoading ? (
            <div className="k-loading">
              <div className="k-loading__head">
                <span className="k-loading__spinner" />
                <span className="k-loading__label">{t("hh.search.loading")}</span>
              </div>
            </div>
          ) : null}
          {/* A 404/error (incl. name-find not-yet-deployed) → calm "no results", not a crash. */}
          {!search.isLoading && (search.error || hits.length === 0) ? (
            <div className="k-empty" data-testid="hh-no-results">
              <div className="k-empty__icon">
                <Icon name="search" size={26} />
              </div>
              <p className="k-empty__title">{t("hh.search.none")}</p>
            </div>
          ) : (
            <ul
              className="hh-hit-list"
              style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 10 }}
            >
              {hits.map((h) => (
                <HitRow key={h.id} hit={h} />
              ))}
            </ul>
          )}
        </div>
      ) : (
        // The default surface — the advisor's own book, visible without searching.
        <div className="hh-list" data-testid="hh-list">
          {/* Server-side filter tabs — switching refetches with the `filter` param. */}
          <div
            className="qf-seg"
            role="tablist"
            aria-label={t("hh.book.tabsLabel")}
            data-testid="hh-book-tabs"
            style={{ marginBottom: 14 }}
          >
            {BOOK_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={filter === tab.id}
                className={`qf-seg__btn${filter === tab.id ? " qf-seg__btn--active" : ""}`}
                data-testid={`hh-book-tab-${tab.id}`}
                onClick={() => setFilter(tab.id)}
              >
                {t(`hh.book.tab.${tab.key}`)}
              </button>
            ))}
          </div>

          <p className="surface__section-label">
            {t("hh.list.title")}
            {!book.isLoading && !book.isError ? (
              <span data-testid="hh-book-count" style={{ marginLeft: 8, color: "var(--k-muted)" }}>
                · {bookCount} {t("hh.book.count")}
              </span>
            ) : null}
          </p>

          {book.isLoading ? (
            <div className="k-loading">
              <div className="k-loading__head">
                <span className="k-loading__spinner" />
                <span className="k-loading__label">{t("hh.book.loading")}</span>
              </div>
            </div>
          ) : book.isError ? (
            <div className="k-empty" data-testid="hh-list-error">
              <div className="k-empty__icon">
                <Icon name="alert-triangle" size={26} />
              </div>
              <p className="k-empty__title">{t("hh.book.error")}</p>
            </div>
          ) : bookRows.length === 0 ? (
            <div className="k-empty" data-testid="hh-list-empty">
              <div className="k-empty__icon">
                <Icon name="household" size={26} />
              </div>
              <p className="k-empty__title">{t("hh.book.empty")}</p>
            </div>
          ) : (
            <ul
              className="hh-hit-list"
              style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 10 }}
            >
              {bookRows.map((h) => (
                <BookRow key={h.householdId} row={h} />
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Advanced — open a household directly by id (the pre-U1 flow, kept as a
          clearly-secondary fallback; advisors don't normally carry a UUID). */}
      <details className="hh-advanced">
        <summary className="k-btn k-btn--ghost k-btn--sm" style={{ cursor: "pointer" }}>
          {t("hh.advanced")}
        </summary>
        <form
          className="hh-open-form"
          style={{ display: "flex", gap: 10, alignItems: "flex-end", marginTop: 12, maxWidth: 560 }}
          onSubmit={(e) => {
            e.preventDefault();
            if (rawId.trim()) navigate(`/households/${rawId.trim()}`);
          }}
        >
          <label className="k-field" style={{ flex: 1, marginBottom: 0 }}>
            <span className="k-field__label">{t("hh.householdId")}</span>
            <input
              className="k-input"
              aria-label={t("hh.householdId")}
              value={rawId}
              onChange={(e) => setRawId(e.target.value)}
            />
          </label>
          <button type="submit" className="k-btn k-btn--secondary" disabled={!rawId.trim()}>
            {t("hh.open")}
          </button>
        </form>
      </details>
    </section>
  );
}

// The dominant policy 5-status as a calm k-status pill — active/renewed are green,
// cancellation-requested a warm review tone, cancelled neutral; only EXPIRED carries the
// genuine-alarm (ended) tone. Reuses the existing status-pill styling (no new palette).
const policyStatusClass = (status: string): string => {
  switch (status) {
    case "ACTIVE":
    case "RENEWED":
      return "k-status--active";
    case "CANCELLATION_REQUESTED":
      return "k-status--review";
    case "EXPIRED":
      return "k-status--ended";
    default:
      return "k-status--pending";
  }
};

// Map the server's lightweight next-action hint to an i18n key; unknown hints fall back to
// the raw string (honest, never a crash).
const NEXT_ACTION_KEYS: Record<
  string,
  "renewal" | "quote" | "lead" | "claim" | "alert" | "service"
> = {
  "renewal approaching": "renewal",
  "quote awaiting": "quote",
  "new lead": "lead",
  "claim follow-up": "claim",
  "alert to review": "alert",
  "service follow-up": "service",
};

// A single book card — always links into the 360 (every book row carries a householdId).
// Shows the MASKED phone the server returns (never a full number), the policy count + a
// dominant-status pill, a DISTINCT dashed renewal-due badge, a calm care-hold badge, and a
// next-action line — all on our DS.
function BookRow({ row }: { row: BookHouseholdSummary }) {
  const { t } = useT();
  const initials = row.name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("");
  const naKey = row.nextAction ? NEXT_ACTION_KEYS[row.nextAction] : undefined;
  const nextActionLabel = row.nextAction
    ? naKey
      ? t(`hh.book.na.${naKey}`)
      : row.nextAction
    : null;

  return (
    <li className="hh-hit k-card k-card--pad-sm" data-testid={`hh-book-${row.householdId}`}>
      <Link
        to={`/households/${row.householdId}`}
        className="hh-hit-link"
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
          textDecoration: "none",
          color: "inherit",
        }}
      >
        <span className="k-avatar">{initials}</span>
        <div style={{ flex: 1, minWidth: 0, display: "grid", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span className="hh-hit-name" style={{ fontWeight: 600, color: "var(--k-ink)" }}>
              {row.name}
            </span>
            <span style={metaText} data-testid={`hh-book-members-${row.householdId}`}>
              · {row.memberCount} {t("hh.book.members")}
            </span>
            {/* MASKED phone only — the server never sends the full number. */}
            {row.phone ? (
              <span style={metaText} data-testid={`hh-book-phone-${row.householdId}`}>
                · {row.phone}
              </span>
            ) : null}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={metaText} data-testid={`hh-book-policies-${row.householdId}`}>
              {row.policyCount} {t("hh.book.policies")}
            </span>
            {row.dominantPolicyStatus ? (
              <span
                className={`k-status ${policyStatusClass(row.dominantPolicyStatus)}`}
                data-testid={`hh-book-status-${row.householdId}`}
              >
                <span className="k-status__dot" />
                {t(`hh.polStatus.${row.dominantPolicyStatus}` as never)}
              </span>
            ) : null}
            {/* Renewal-due — DISTINCT from the status pills (dashed amber hairline, no fill). */}
            {row.renewalDue ? (
              <span
                className="k-badge k-badge--warning"
                data-testid={`hh-book-renewal-${row.householdId}`}
                style={{ background: "transparent", border: "1px dashed var(--k-amber-border)" }}
              >
                {t("hh.book.renewalDue")}
              </span>
            ) : null}
            {/* Care-hold — a CALM accent-soft badge (not alarm): sales muted, not a warning. */}
            {row.careHold ? (
              <span
                className="k-badge k-badge--premier"
                data-testid={`hh-book-carehold-${row.householdId}`}
              >
                {t("hh.book.careHold")}
              </span>
            ) : null}
          </div>

          {nextActionLabel ? (
            <div style={metaText} data-testid={`hh-book-nextaction-${row.householdId}`}>
              {t("hh.book.nextAction")}: {nextActionLabel}
            </div>
          ) : null}
        </div>
        <Icon name="arrow-right" size={16} style={{ marginTop: 2 }} />
      </Link>
    </li>
  );
}

// A single party row — links into the 360 when a household id is present, otherwise
// shows the match but flags it as not-yet-openable (the phone-find carries no household
// id; only U1-B's name-find resolves it).
function HitRow({ hit: h }: { hit: PartyHit }) {
  const { t } = useT();
  const name = `${h.firstName} ${h.lastName}`.trim();
  const initials = (h.firstName?.[0] ?? "") + (h.lastName?.[0] ?? "");
  return (
    <li className="hh-hit k-card k-card--pad-sm" data-testid={`hh-hit-${h.id}`}>
      {h.householdId ? (
        <Link
          to={`/households/${h.householdId}`}
          className="hh-hit-link"
          style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}
        >
          <span className="k-avatar">{initials}</span>
          <span className="hh-hit-name" style={{ fontWeight: 600, color: "var(--k-ink)" }}>
            {name}
          </span>
          {h.phone ? <span style={metaText}> · {h.phone}</span> : null}
          <Icon name="arrow-right" size={16} style={{ marginLeft: "auto" }} />
        </Link>
      ) : (
        <div
          className="hh-hit-flagged"
          style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}
        >
          <span className="k-avatar">{initials}</span>
          <span className="hh-hit-name" style={{ fontWeight: 600, color: "var(--k-ink)" }}>
            {name}
          </span>
          {h.phone ? <span style={metaText}> · {h.phone}</span> : null}
          <span
            className="k-badge k-badge--warning"
            data-testid={`hh-hit-nohousehold-${h.id}`}
            style={{ marginLeft: "auto" }}
          >
            {t("hh.search.noHousehold")}
          </span>
        </div>
      )}
    </li>
  );
}

const metaText = { fontSize: 12.5, color: "var(--k-muted)" } as const;
